import { useState, VFC } from "react";

import { Button } from "@portex-pro/ui-components";
import { ContactType } from "api/rest/partners/types/domain";
import PartnerTable from "components/partners/PartnerTable";
import SimpleSearchView from "components/SimpleSearchView";
import AddTeamMemberButton from "features/contacts/add-team-member/AddTeamMemberButton";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import StepCollapse from "../../_components/StepCollapse";
import { useCreateStepCollapseProps } from "../../_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { FinalSchema } from "../_schemas";

interface TeamMembersStepTagProps {
  allTeamMembers: ContactType[];
  selectedIds: Set<string>;
}

const TeamMembersStepTag: VFC<TeamMembersStepTagProps> = ({ allTeamMembers, selectedIds }) => {
  if (selectedIds.size === 0) {
    return <></>;
  }

  const selectedMemberNames = allTeamMembers
    .filter((member) => selectedIds.has(member.id.toString()))
    .map((member) => {
      const firstName = member.first_name || "";
      const lastName = member.last_name || "";
      const fullName = [firstName, lastName].filter(Boolean).join(" ");
      return fullName || member.user.email;
    })
    .filter(Boolean);

  const uniqueMemberNames = Array.from(new Set(selectedMemberNames));

  if (uniqueMemberNames.length === 0) {
    return <></>;
  }

  let displayText = "";
  if (uniqueMemberNames.length <= 2) {
    displayText = uniqueMemberNames.join(", ");
  } else {
    const extraCount = selectedIds.size - 2;
    displayText = `${uniqueMemberNames.slice(0, 2).join(", ")} +${extraCount}`;
  }

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      <span className="text-blue-600 font-bold text-sm">{displayText}</span>
    </div>
  );
};

const TeamStep: VFC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["quoteRequest", "common", "shipper"]);
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const { stepIndex, quoteRequest: quoteRequest } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const [search, setSearch] = useState<string>();
  const [allTeamMembers, setAllTeamMembers] = useState<ContactType[]>([]);
  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Team,
    name: t("shipper:teamMembers"),
    number: 6,
  });
  const methods = useFormContext<FinalSchema>();

  const { field, fieldState } = useController({ control: methods.control, name: "teammates" });

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Review);
    setIsOpen(false);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={
        <TeamMembersStepTag
          allTeamMembers={allTeamMembers}
          selectedIds={new Set(field.value?.map(({ contact_id }) => contact_id.toString()) || [])}
        />
      }
      footer={
        stepIndex <= QuoteStepIndex.Team ? (
          <div className="flex justify-between mt-2 w-full items-center">
            {fieldState.error ? <p className="text-sm text-red-600">{fieldState.error.message}</p> : <div />}
            <div className="flex items-center">
              {!!quoteRequest.teammates?.length && quoteRequest.teammates.length > 0 && (
                <span className="text-sm text-gray-600 pr-2">
                  {t("shipper:teammembersSelected", { count: quoteRequest.teammates.length })}
                </span>
              )}
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={methods.handleSubmit(submit)}
                style={{ width: 150 }}
              >
                {t("common:next")}
              </Button>
            </div>
          </div>
        ) : null
      }
    >
      <div className="flex w-full mt-6">
        <PartnerTable
          partnerType="internal"
          wrapInScrollable
          containerClassName="min-h-[700px] w-full flex flex-col"
          showPagination
          selectedPartnerIds={new Set(field.value?.map(({ contact_id }) => contact_id.toString()))}
          actionsSlot={<AddTeamMemberButton />}
          searchValue={search}
          onPartnersLoaded={(partners) => {
            setAllTeamMembers((prevMembers) => {
              const memberMap = new Map(prevMembers.map((m) => [m.id, m]));

              partners.forEach((partner) => {
                memberMap.set(partner.id, partner);
              });

              return Array.from(memberMap.values());
            });
          }}
          searchSlot={
            <SimpleSearchView
              search={search ?? ""}
              onChange={(search) => setSearch(search)}
              placeholder={t("shipper:searchTeamMembers")}
            />
          }
          headerSlot={
            <div className="px-4 mb-4">
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
                <b>{t("shipper:allTeamMembersCCd")}</b>
              </div>
            </div>
          }
          onSelectionChange={(selectedIds) =>
            setQuoteRequestAction({
              teammates: Array.from(selectedIds).map((contactId) => ({ contact_id: Number(contactId) })),
            })
          }
        />
      </div>
    </StepCollapse>
  );
};

export default TeamStep;
